import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { ISiteState, setSiteFetchStatus } from 'features/site/siteSlice';
import {
  deletePageData,
  fetchPages,
  IPage,
  patchPageData,
  postPageData,
} from './pageAPI';

export interface IPagesState {
  pages: IPage[];
  status: 'idle' | 'loading' | 'failed';
  errors: any;
}

export type PagesStateProps = {
  pages: IPage[];
};

export const initialState: IPagesState = {
  pages: [],
  status: 'idle',
  errors: '',
};

export const fetchPagesAsync = createAsyncThunk('page/fetchPages', async () => {
  const res = await fetchPages();
  return { response: res };
});

export const postPageAsync = createAsyncThunk(
  'page/postPage',
  async (
    {
      data,
    }: {
      data: IPage;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await postPageData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchPageAsync = createAsyncThunk(
  'page/patchPage',
  async (
    {
      data,
    }: {
      data: IPage;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await patchPageData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deletePageAsync = createAsyncThunk(
  'page/deletePage',
  async (
    {
      id,
    }: {
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await deletePageData(id) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export function addPageCases(builder: ActionReducerMapBuilder<ISiteState>) {
  builder
    .addCase(fetchPagesAsync.pending, (state) => {
      state.status = 'loading';
      setSiteFetchStatus(state, 'pages', 'loading');
    })
    .addCase(fetchPagesAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      setSiteFetchStatus(state, 'pages', 'idle');
      state.pages = action.payload.response.data;
    })
    .addCase(fetchPagesAsync.rejected, (state, action) => {
      state.status = 'failed';
      setSiteFetchStatus(state, 'pages', 'failed');
      state.errors = action.payload;
    })
    .addCase(postPageAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postPageAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const id = action.payload?.response?.data?._id;
      if (id) {
        const data = { ...action.meta.arg.data, id };
        state.pages.push(data);
      }
    })
    .addCase(postPageAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchPageAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchPageAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const p = state.pages.find((p) => p.id === action.meta.arg.data.id);
      if (p) {
        Object.assign(p, action.meta.arg.data);
      }
    })
    .addCase(patchPageAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deletePageAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deletePageAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.pages = state.pages.filter((p) => p.id !== action.meta.arg.id);
    })
    .addCase(deletePageAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });
}
